import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { Fragment, useState } from 'react';
import toast from 'react-hot-toast';
import { listSilobagsService, type SilobagListItem } from '@siloreal:shared:api/service/silobag/silobag-list';
import type { ExplorerServiceAdapterState } from '@siloreal:shared:ui/collection/explorer';
import * as UI from '@siloreal:shared:ui';
import { downloadSilobagReport } from '../../../extra/silobag-report-export';
import { useLabTemplateDownload } from '../../../hook/use-lab-template-download';
import { useSatelliteCertification } from '../../../hook/use-satellite-certification';
import { LabTemplateModal } from '../../lab-template-modal';
import { SatelliteReportModal } from '../../satellite-report-modal';
import { toSilobagListContract } from '../service/adapter';
import {
  getExportConfig,
  type ExportableSilobagList,
  type ExportListConfig,
  type ExportMenuItem,
  type ExportMode,
} from './export-menu-config';
import * as HOOKS from '@siloreal:shared:hooks';

const EXPORT_PAGE_SIZE = 9_999_999_999;
const EXPORT_PAGINATION = { page: 0, pageSize: EXPORT_PAGE_SIZE };

type ExportInteractionProps = {
  interactionId: string;
  list: ExportableSilobagList;
  disabled?: boolean;
};

type ExportRequest = {
  list: ExportableSilobagList;
  mode: ExportMode;
  serviceState: ExplorerServiceAdapterState;
  selectedIds?: string[];
};

const EMPTY_SERVICE_STATE: ExplorerServiceAdapterState = {
  paginationModel: EXPORT_PAGINATION,
  sortingModel: [],
  liveSearch: '',
  activeDrawerFilters: {},
};

function getSelectedIds(selectionModel: GridRowSelectionModel): string[] {
  return Array.from(selectionModel.ids).map(String);
}

async function getSelectedRows(selectedIds: string[]): Promise<SilobagListItem[]> {
  const selectedRows = await Promise.all(
    selectedIds.map(async (id) => {
      const response = await listSilobagsService({
        pagination: { page: 0, take: 10 },
        filter: { id },
      });

      return response.data.find((row) => String(row.id) === id);
    })
  );

  if (selectedRows.some((row) => !row)) {
    throw new Error('No se pudieron obtener todas las silobolsas seleccionadas.');
  }

  return selectedRows as SilobagListItem[];
}

async function getRowsToExport(request: ExportRequest): Promise<SilobagListItem[]> {
  if (request.mode === 'selection') {
    return getSelectedRows(request.selectedIds ?? []);
  }

  const config = getExportConfig(request.list);
  const serviceState =
    request.mode === 'filtered'
      ? { ...request.serviceState, paginationModel: EXPORT_PAGINATION }
      : EMPTY_SERVICE_STATE;
  const contract = toSilobagListContract(serviceState, { implicitFilters: config.filters });
  const response = await listSilobagsService(contract);

  return response.data;
}

async function getIdsToExport(request: ExportRequest): Promise<string[]> {
  if (request.mode === 'selection') {
    return request.selectedIds ?? [];
  }

  const config = getExportConfig(request.list);
  const serviceState =
    request.mode === 'filtered'
      ? { ...request.serviceState, paginationModel: EXPORT_PAGINATION }
      : EMPTY_SERVICE_STATE;
  const contract = toSilobagListContract(serviceState, { implicitFilters: config.filters });
  const response = await listSilobagsService({
    ...contract,
    onlyIds: true,
  });

  return response.ids.map(String);
}

function getExportFileName(config: ExportListConfig, mode: ExportMode): string {
  const today = new Date();
  const date = [
    String(today.getDate()).padStart(2, '0'),
    String(today.getMonth() + 1).padStart(2, '0'),
    today.getFullYear(),
  ].join('-');
  const modeLabel = {
    list: 'Listado completo',
    selection: 'Selección',
    filtered: 'Vista actual',
  } satisfies Record<ExportMode, string>;

  return [config.fileLabel, date, modeLabel[mode]].join(' - ');
}

async function exportSilobags(request: ExportRequest): Promise<number> {
  const config = getExportConfig(request.list);
  const rows = await getRowsToExport(request);

  return downloadSilobagReport(rows, getExportFileName(config, request.mode));
}

export function ExportInteraction(props: ExportInteractionProps) {
  const { interactionId, list, disabled } = props;
  const explorer = UI.collection.useExplorer();
  const config = getExportConfig(list);
  const labTemplates = useLabTemplateDownload();
  const satelliteCertification = useSatelliteCertification();
  const [isExporting, setIsExporting] = useState(false);
  const qualitySelectionActionId = `${interactionId}-quality-samples`;
  const satelliteSelectionActionId = `${interactionId}-satellite-certificate`;
  const i18n = HOOKS.useLocale();

  const executeExport = async (mode: ExportMode, selectedIds?: string[]): Promise<void> => {
    setIsExporting(true);
    const loadingToastId = toast.loading(`${i18n.t('temp.common.toasts.loading.generating_excel')} ...`);

    try {
      const exportedCount = await exportSilobags({
        list,
        mode,
        serviceState: explorer.serviceState,
        selectedIds,
      });

      toast.success(
        `${i18n.t('temp.common.toasts.success.exported_part_1')} ${exportedCount} ${i18n.t('temp.common.toasts.success.exported_part_2')}`,
        {
          id: loadingToastId,
        }
      );
    } catch {
      toast.error(i18n.t('temp.common.toasts.error.export_failed'), { id: loadingToastId });
    } finally {
      setIsExporting(false);
    }
  };

  const handleSelectionConfirm = (selectionModel: GridRowSelectionModel): Promise<void> => {
    return executeExport('selection', getSelectedIds(selectionModel));
  };

  const handleSelectionClick = (): void => {
    explorer.startSelection({
      id: interactionId,
      onConfirm: handleSelectionConfirm,
    });
  };

  const handleListClick = (): void => {
    void executeExport('list');
  };

  const handleFilteredClick = (): void => {
    void executeExport('filtered');
  };

  const executeQualitySampleDownload = (mode: ExportMode, selectedIds?: string[]): Promise<void> => {
    return labTemplates.open(() =>
      getIdsToExport({
        list,
        mode,
        serviceState: explorer.serviceState,
        selectedIds,
      })
    );
  };

  const handleQualitySelectionConfirm = (selectionModel: GridRowSelectionModel): Promise<void> => {
    return executeQualitySampleDownload('selection', getSelectedIds(selectionModel));
  };

  const handleQualitySelectionClick = (): void => {
    explorer.startSelection({
      id: qualitySelectionActionId,
      onConfirm: handleQualitySelectionConfirm,
    });
  };

  const handleQualityListClick = (): void => {
    void executeQualitySampleDownload('list');
  };

  const handleQualityFilteredClick = (): void => {
    void executeQualitySampleDownload('filtered');
  };

  const executeSatelliteCertification = (mode: ExportMode, selectedIds?: string[]): Promise<void> => {
    return satelliteCertification.generate(() =>
      getIdsToExport({
        list,
        mode,
        serviceState: explorer.serviceState,
        selectedIds,
      })
    );
  };

  const handleSatelliteSelectionConfirm = (selectionModel: GridRowSelectionModel): Promise<void> => {
    return executeSatelliteCertification('selection', getSelectedIds(selectionModel));
  };

  const handleSatelliteSelectionClick = (): void => {
    explorer.startSelection({
      id: satelliteSelectionActionId,
      onConfirm: handleSatelliteSelectionConfirm,
    });
  };

  const handleSatelliteListClick = (): void => {
    void executeSatelliteCertification('list');
  };

  const handleSatelliteFilteredClick = (): void => {
    void executeSatelliteCertification('filtered');
  };

  const handleItemClick = (item: ExportMenuItem): (() => void) | undefined => {
    if (item.kind === 'quality-samples') {
      if (item.mode === 'selection') return handleQualitySelectionClick;
      if (item.mode === 'filtered') return handleQualityFilteredClick;

      return handleQualityListClick;
    }

    if (item.kind === 'report') {
      if (item.mode === 'selection') return handleSelectionClick;
      if (item.mode === 'filtered') return handleFilteredClick;

      return handleListClick;
    }

    if (item.kind === 'satellite-certificate') {
      if (item.mode === 'selection') return handleSatelliteSelectionClick;
      if (item.mode === 'filtered') return handleSatelliteFilteredClick;

      return handleSatelliteListClick;
    }

    return undefined;
  };

  const getItemTestId = (item: ExportMenuItem): string => {
    const suffix = item.kind === 'report' ? item.mode : item.id;
    return `${interactionId}-${suffix}-btn`;
  };

  return (
    <>
      <UI.dropdown.base
        variant="alternative"
        label={HOOKS.useLocale().t('temp.common.export')}
        icon={<KeyboardArrowDownRoundedIcon fontSize="small" />}
        iconPosition="end"
        disabled={disabled || isExporting || labTemplates.isResolving || satelliteCertification.isGenerating}
        closeOnContentClick
        tid={`${interactionId}-btn`}
        popupTid={`${interactionId}-popup`}
        menuAriaLabel="Opciones de exportación"
      >
        {config.groups.map((group) => (
          <Fragment key={group.key}>
            <UI.dropdown.section>{group.title}</UI.dropdown.section>
            {group.items.map((item) => (
              <UI.dropdown.option
                key={item.id}
                badge={item.format}
                onClick={handleItemClick(item)}
                tid={getItemTestId(item)}
              >
                {item.label}
              </UI.dropdown.option>
            ))}
          </Fragment>
        ))}
      </UI.dropdown.base>
      <LabTemplateModal
        open={labTemplates.isModalOpen}
        silobagIds={labTemplates.silobagIds}
        onClose={labTemplates.closeModal}
      />
      <SatelliteReportModal
        open={satelliteCertification.isModalOpen}
        onClose={satelliteCertification.closeModal}
      />
    </>
  );
}
